import React from 'react';
import _ from 'lodash';
import Card from '../dish-menu-creator/Card';
import GlobalNotification from '../../component/common/snackbar';
import { Box, Grid, Typography } from '@mui/material';
import { SubFunctionAuxiliaryHint } from '../rbp-cloud-sub-function/sub-function-auxiliary-hint.component';
import i18n from '../../i18n';
import { getRBPStringId } from '../main/main-page-states';
import apiManage from '../../request';
import { RestaurantStaticImageUpload } from '../../component/restaurant-static-image-upload.component';
import { FirstScreenBundledContext, type FirstScreenBundledContextValue } from './first-screen-bundled-context';
import {
    BUNDLED_DISH_DEFAULT_IMAGE_FILE,
    BUNDLED_DISH_DEFAULT_SMALL_FILE,
    withImageCacheBust,
} from '../../utils/restaurant-static-bundled';
import LSM from '../../modules/cache/tool-local-storage';
import { LOCAL_STORAGE_KEYS } from '../../modules/cache/local-storage.constant';

/**
 * 基础设置卡片：餐厅环境图 + 菜品默认图；展示地址来自 bundled，上传走 v1 `restaurant-static`。
 */
export default class BackgroundImage extends React.Component<Props> {
    /** 首页 bundled Context */
    public static contextType = FirstScreenBundledContext;

    /**
     * React 注入的 Context（避免 `declare context` 导致 CRA Babel 构建失败）。
     */
    private get bundledPageContext(): FirstScreenBundledContextValue | null {
        return (this as unknown as { context: FirstScreenBundledContextValue | null }).context;
    }

    /** 静态资源根（bundled 未返回环境图时的回退） */
    public SERVER_URL = LSM.get(LOCAL_STORAGE_KEYS.SERVER_URL);

    /**
     * 绑定删除默认菜品图方法引用（历史构造函数习惯）。
     */
    public constructor(props: Props) {
        super(props);
        this.deleteImage = this.deleteImage.bind(this);
    }

    /**
     * 删除菜品默认图（大小图），与上传文件名一致。
     */
    async deleteImage() {
        const sid = getRBPStringId();
        const rMain = await apiManage.delete_restaurant_static_dish_image(sid, BUNDLED_DISH_DEFAULT_IMAGE_FILE);
        const rSmall = await apiManage.delete_restaurant_static_dish_image(sid, BUNDLED_DISH_DEFAULT_SMALL_FILE);
        const failed = Boolean(_.get(rMain, 'error')) && Boolean(_.get(rSmall, 'error'));
        if (failed) {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            return;
        }
        GlobalNotification.success(i18n.t('global_text_operate_success'));
        await this.bundledPageContext?.reloadBundled();
    }

    /**
     * 修改餐厅环境图（v1 `bulletin-background-image`）。
     * @param file - 上传文件
     */
    async onChangeBackGround(file: File) {
        const sid = getRBPStringId();
        const res = await apiManage.post_restaurant_static_bulletin_background_image(sid, file);
        if (_.get(res, 'error')) {
            GlobalNotification.error(i18n.t('first_screen_setting_page_modify_restaurant_background_picture_failed'));
        } else {
            GlobalNotification.success(i18n.t('first_screen_setting_page_modify_restaurant_background_picture_succeed'));
            await this.bundledPageContext?.reloadBundled();
        }
    }

    /**
     * 菜品默认图片：大图、小图各上传一次（同一文件，query `file_name` 区分）。
     * @param file - 上传文件
     */
    async onChangeMenuDefault(file: File) {
        const sid = getRBPStringId();
        const rMain = await apiManage.post_restaurant_static_dish_image(sid, file, BUNDLED_DISH_DEFAULT_IMAGE_FILE);
        if (_.get(rMain, 'error')) {
            GlobalNotification.error(i18n.t('first_screen_setting_page_modify_default_dishes_picture_failed'));
            return;
        }
        const rSmall = await apiManage.post_restaurant_static_dish_image(sid, file, BUNDLED_DISH_DEFAULT_SMALL_FILE);
        if (_.get(rSmall, 'error')) {
            GlobalNotification.error(i18n.t('first_screen_setting_page_modify_default_dishes_picture_failed'));
            return;
        }
        GlobalNotification.success(i18n.t('first_screen_setting_page_modify_default_dishes_picture_succeed'));
        await this.bundledPageContext?.reloadBundled();
    }

    /**
     * 渲染基础设置区。
     */
    render() {
        /** 云 RBP 子功能顶栏「辅助模式」：开启时在卡头下展示设计稿说明 */
        const auxiliaryModeEnabled = this.props.auxiliaryModeEnabled === true;
        const ctx = this.bundledPageContext;
        const bust = ctx?.bustVersion ?? 0;
        const bg = String(ctx?.bundled.backgroundUrl ?? '').trim();
        const dishSmall = String(ctx?.bundled.dishDefault.smallUrl ?? '').trim();
        const dishLarge = String(ctx?.bundled.dishDefault.imageUrl ?? '').trim();
        /** bundled 无环境图时回退旧静态根（迁移期兼容） */
        const backgroundDisplay =
            withImageCacheBust(bg, bust) ??
            (this.SERVER_URL ? `${String(this.SERVER_URL).replace(/\/+$/, '')}/back_ground_image.jpg?cb=${bust}` : undefined);
        const menuDefaultSmall =
            withImageCacheBust(dishSmall, bust) ??
            (this.SERVER_URL
                ? `${String(this.SERVER_URL).replace(/\/+$/, '')}/menu_default_s.jpg?cb=${bust}`
                : undefined);
        const menuDefaultLarge =
            withImageCacheBust(dishLarge, bust) ??
            (this.SERVER_URL
                ? `${String(this.SERVER_URL).replace(/\/+$/, '')}/menu_default.jpg?cb=${bust}`
                : undefined);
        const sid = getRBPStringId();

        return (
            /* 首行与「顶部文字公告」等高：外层参与 flex 拉伸（Figma 1838:52035） */
            <Box className="first-screen-basic-setting-root" sx={{ height: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Card
                className="common-card"
                title={(
                    <Box sx={{ width: '100%' }}>
                        <Typography variant="subtitle1" sx={{ color: 'var(--text-primary-300)' }}>
                            {i18n.t('base_setting_title')}
                        </Typography>
                        <SubFunctionAuxiliaryHint
                            enabled={auxiliaryModeEnabled}
                            i18nKey="first_screen_aux_basic_setting"
                        />
                    </Box>
                )}
                contentRender={(
                    <Grid container spacing={3}>
                        <Grid item>
                            <RestaurantStaticImageUpload
                                stringId={sid}
                                kind="dish-image"
                                uploadEntityId={1}
                                deferStaticUpload
                                imageRef={bg}
                                displayUrl={backgroundDisplay ?? ''}
                                avatarLabel={i18n.t('first_screen_setting_page_restaurant_background_picture')}
                                avatarCaptionLayout="firstScreenSlot"
                                onStagedStaticFile={async (file) => {
                                    await this.onChangeBackGround(file);
                                }}
                                avatarSize={120}
                                sx={{ width: 120, alignSelf: 'flex-start' }}
                            />
                        </Grid>
                        <Grid item>
                            <RestaurantStaticImageUpload
                                stringId={sid}
                                kind="dish-image"
                                uploadEntityId={1}
                                deferStaticUpload
                                imageRef={dishSmall || dishLarge}
                                displayUrl={menuDefaultSmall ?? ''}
                                displayUrlMax={menuDefaultLarge ?? menuDefaultSmall ?? ''}
                                avatarLabel={i18n.t('first_screen_setting_page_dishes_default_picture')}
                                avatarCaptionLayout="firstScreenSlot"
                                onStagedStaticFile={async (file) => {
                                    await this.onChangeMenuDefault(file);
                                }}
                                onDeferredStaticRemove={async () => {
                                    await this.deleteImage();
                                }}
                                avatarSize={120}
                                sx={{ width: 120, alignSelf: 'flex-start' }}
                            />
                        </Grid>
                    </Grid>
                )}
            />
            </Box>
        );
    }
}

type Props = {
    /** 云 RBP 子功能壳顶栏「辅助模式」是否开启 */
    auxiliaryModeEnabled?: boolean;
};
